import { Helmet } from "react-helmet";
import LiveDemoForm from "components/Layouts/LiveDemoForm/LiveDemoForm";
import SupportSection from "components/Layouts/Support/SupportSection";
import duoplaneImage from "assets/images/duoplane-skulabs.png";

import {
  IntegrationLogos,
  VideoSection,
  IntegraionFeaturesSection,
  TargetIntegrationSection,
  Capabilities,
} from "components/Layouts/Integrations";

interface IntegrationsProps {
  data: {
    seoTitle?: string;
    seoDescription?: string;
    squareLogo?: () => Promise<string>;
    videoPreview?: string;
    video?: string;
    companyName: string;
    category: string;
    content?: React.ReactNode;
    features?: {
      title: string;
      description: string;
    }[];
  };
}

const defaultPageDescription =
  "See stock levels for all of your products at a glance, track inventory across multiple warehouses, and sync inventory changes to all of your sales channels";

const categoryDescriptions: { [key: string]: string } = {
  Channel:
    "Manage your sales channels with ease. Your entire online operations in one simple interface.",
  Shipping:
    "Ship faster and smarter. Intelligent shipping automation software for warehouses, retailers, and online sellers.",
  "Accounting/Payments":
    "Connect SKULabs to your accounting and payment providers to streamline your business.",
  Other:
    "SKULabs offers connections to many online tools - contact us to add an integration.",
  "3PL":
    "Manage your 3PL with ease. SKULabs integrates with ShipBob, ShipMonk, and more.",
};

const defaultSeoDescription =
  "integrations with amazon, shopify, walmart, woocommerce, ebay, fedex, usps, ups";

const Integrations: React.FC<IntegrationsProps> = ({ data }) => {
  const {
    seoTitle,
    seoDescription,
    category,
    companyName,
    squareLogo,
    videoPreview,
    video,
    features,
    content,
  } = data;
  const bgImage = "/images/hero-singleintegrations.webp";

  return (
    <>
      <Helmet>
        <title>
          {seoTitle || `Streamline ${companyName} Management with SKULabs`}
        </title>
        <meta
          name="description"
          content={seoDescription || defaultSeoDescription}
        />
      </Helmet>
      <section
        style={{ backgroundImage: `url(${bgImage})` }}
        className={
          video ? "hero-integration extra-bottom-padding" : "hero-integration"
        }
      >
        <h1>
          {category === "Channel" && (
            <>
              Simplify Shipping and Inventory Management for your{" "}
              <span className="accent">{companyName}</span> store
            </>
          )}
          {category === "Shipping" && (
            <>
              Simply print <span className="accent">{companyName}</span> labels
              for your online store
            </>
          )}
          {category === "Accounting/Payments" && (
            <>
              Better accounting <br /> with{" "}
              <span className="accent">{companyName}</span> +{" "}
              <span className="accent">SKULabs</span>
            </>
          )}
          {category === "3PL" && (
            <>
              Keep track of your <span className="accent">{companyName}</span>{" "}
              operations on <span className="accent">SKULabs</span>
            </>
          )}
          {category === "Other" && (
            <>
              <span className="accent">SKULabs</span> integrates with{" "}
              <span className="accent">{companyName}</span>
            </>
          )}
        </h1>
        <p className="description">
          {seoDescription ||
            categoryDescriptions[category] ||
            defaultPageDescription}
        </p>
        {squareLogo && (
          <IntegrationLogos
            companyMiniLogo={squareLogo}
            companyName={companyName}
          />
        )}
      </section>
      <main className="single-integration">
        <section className="made-simple container">
          {video && (
            <VideoSection video={video} previewVideoDesc={videoPreview} />
          )}
          <Capabilities category={category} companyName={companyName} />
          <div className="live-demo">
            <hr />
            <LiveDemoForm />
            <hr />
          </div>
          <IntegraionFeaturesSection data={features} />
          {companyName === "Duoplane" && (
            <>
              <img
                className="connection-image"
                src={duoplaneImage}
                alt="Duoplane plus SKULabs"
              />
            </>
          )}
          <div className="more-information">{content}</div>
        </section>
      </main>
      <TargetIntegrationSection />
      <SupportSection />
    </>
  );
};

export default Integrations;
